import {MicrositePoolsState, PoolScope, PoolsMenuScope} from './pool.models';
import { poolMenuAdapter, initialPoolMenuState } from './pool-menu.adapter';
import {
  callFantasyStats,
  callPool, callSportsStats,
  clearAllPoolSelections,
  clearCurrentLines,
  clearPool,
  failPools,
  generateTicket,
  markCrossSell,
  markSmartpick,
  partialUpdate,
  removeLastSelectionInLeg,
  removeSelectionsInLeg,
  setPoolsExtraDetails,
  setSportCode, successFantasyStats,
  successLeftMenu,
  successPool,
  successPoolFeatured,
  successPoolsHeadline,
  successPoolsMenu, successSportsStats,
  toggleSelection
} from './pool.actions';
import {Action, createReducer, on} from '@ngrx/store';
import {successCoreSettings} from '../../NGRX/configuration/configuration.actions';
import * as _ from 'lodash-es';
import * as Big from 'big.js';
import {PoolSelections} from '../../services/selections/selections.model';
import {
  calculateLines,
  calculateSelectionsString,
  calculateSelectionsStringVotes,
  clearLines,
  updateValidLegs
} from './parseSelections';
import {
  toggleSelectionInLeg as toggleSelectionUtil,
  removeSelectionsInLeg as removeSelectionsUtil,
  removeLastSelectionInLeg as removeLastSelectionUtil,
  clearAutoPickFlags
} from './pool.utils';

export const initialState: MicrositePoolsState = {
  currentPool: undefined,
  currentPoolLoading: false,
  currentPoolLoaded: false,
  currentPoolSelections: {},
  featuredPool: undefined,
  parsedLeftMenu: undefined,

  // Entity-managed pools state
  poolMenuEntities: initialPoolMenuState,

  // Legacy menu (kept for backward compatibility)
  menu: undefined,

  menuLoaded: false,
  poolsExtraModel: {
    poolCounts: {},
    POOLS_VALUE: new Big.Big(0),
    CURRENCY_CODE: 'GBP',
    OPEN_SYNDICATES: 0,
    BDG: []
  },
  filters: undefined,
  currentSportCode: {},
  headlinePools: undefined,
  sportsStats: undefined,
  fantasyStats: undefined,
  sportsStatsLoading: false,
  fantasyStatsLoading: false
};


const reducer = createReducer(
  initialState,

  on(callPool, (state, action) => {
    // Preserve the current pool data when re-fetching the same pool (e.g. session refresh
    // on login). Clearing it would cause a FOUC because selectEnrichedPool emits undefined
    // for the window between callPool and successPool. For navigation (different pool ID),
    // we still clear so stale data from the previous pool is never shown.
    const isSamePool = state.currentPool?.id !== undefined &&
                       Number(state.currentPool.id) === Number(action.id);
    return {
      ...state,
      currentPool: isSamePool ? state.currentPool : undefined,
      currentPoolLoading: true,
      currentPoolLoaded: false
    };
  }),

  on(failPools, (state) => {
    return {
      ...state,
      currentPoolLoading: false,
      currentPoolLoaded: false
    };
  }),

  on(successPool, (state, action) => {
    if (_.isEqual(state.currentPool, action.pool) && state.currentPoolLoaded === true) {
      return state;
    }

    const initialCurrentPoolSelections: any = {
      id: action.pool.id,
      leg_num: action.pool.leg_num,
      type_code: action.pool.type_code,

      legs_with_selections: 0,
      selections_string: '',
      votes_string: '',
      total_lines: 0,
      smartpick: 0,
      cross_sell: 0,
      legs: {},
      valid_legs: {},

      sep_correlations: action.pool?.leg_correlations,
    };

    let poolSelections = initialCurrentPoolSelections;

    try {
      if (action.pool) {
        if (state.currentPoolSelections && state.currentPoolSelections[action.pool.id]) {
          poolSelections = _.cloneDeep(state.currentPoolSelections[action.pool.id])
        }
      }
    } catch (e) {
      console.error('[Pool Reducer] Error cloning selections:', e);
    }

    const parseSelections = _.flow([calculateLines, calculateSelectionsString, calculateSelectionsStringVotes, updateValidLegs]);

    return {
      ...state,
      currentPool: action.pool,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [`${action.pool.id}`]: parseSelections(poolSelections)
      },
      currentPoolLoading: false,
      currentPoolLoaded: true
    }
  }),

  on(toggleSelection, (state, action) => {
    // GT context is handled by the GroupTickets feature reducer
    if (action.context && (action.context === 'grouped_tickets' || action.context === 'all')) {
      return state;
    }

    // Pre-existing assumption: currentPool is set whenever this action fires (user must be viewing a pool to toggle a selection) — not independently verified
    const poolId = state.currentPool!.id;
    const currentSelections = state.currentPoolSelections[poolId];
    const updatedSelections = clearAutoPickFlags(
      toggleSelectionUtil(currentSelections, action.legId, action.bin)
    );

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [poolId]: updatedSelections
      }
    };
  }),

  on(clearAllPoolSelections, (state, action) => {
    return {
      ...state,
      currentPoolSelections: {}
    }
  }),

  on(generateTicket, (state, action) => {
    const parseSelections = _.flow([calculateLines, calculateSelectionsString, calculateSelectionsStringVotes, updateValidLegs]);

    // clear it's lines

    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const currentPoolId = state.currentPool!.id;
    const cross_sell = state.currentPoolSelections[currentPoolId].cross_sell;
    const smartpick = state.currentPoolSelections[currentPoolId].smartpick;
    const currentPoolSelections: PoolSelections = Object.assign({},
      clearLines(_.cloneDeep(state.currentPoolSelections[currentPoolId])),
      { cross_sell: cross_sell },
      { smartpick: smartpick }
    );

    // add new legs
    const newLegs: any = {};
    const legs = action.selections.split('/');
    _.each(legs, (leg, index) => {
      const bins = leg.split(',');
      newLegs[index + 1] = [];
      _.each(bins, function (bin) {
        newLegs[index + 1].push(Number(bin));
      });
    });
    currentPoolSelections.legs = newLegs;

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [`${currentPoolId}`]: parseSelections(currentPoolSelections)
      }
    }
  }),

  on(markSmartpick, (state, action) => {
    // GT context is handled by the GroupTickets feature reducer
    if (action.context && (action.context === 'grouped_tickets' || action.context === 'all')) {
      return state;
    }

    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const currentPoolId = state.currentPool!.id;
    const currentPoolSelections: PoolSelections = _.cloneDeep(state.currentPoolSelections[currentPoolId]);
    currentPoolSelections.smartpick = action.value || 1;

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [`${currentPoolId}`]: currentPoolSelections
      }
    }
  }),

  on(markCrossSell, (state, action) => {
    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const currentPoolId = state.currentPool!.id;
    const currentPoolSelections: PoolSelections = _.cloneDeep(state.currentPoolSelections[currentPoolId]);
    currentPoolSelections.smartpick = 0; // on the normal service, smartpick becomes 1 after ticket generation :-??
    currentPoolSelections.cross_sell = 1;

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [`${currentPoolId}`]: currentPoolSelections
      }
    }
  }),

  on(clearCurrentLines, (state, action) => {
    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const currentPoolId = state.currentPool!.id;
    const currentPoolSelections: PoolSelections = clearLines(_.cloneDeep(state.currentPoolSelections[currentPoolId]));

    const parseSelections = _.flow([calculateLines, calculateSelectionsString, calculateSelectionsStringVotes, updateValidLegs]);

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [`${currentPoolId}`]: parseSelections(currentPoolSelections)
      }
    }
  }),

  on(removeLastSelectionInLeg, (state, action) => {
    // GT context is handled by the GroupTickets feature reducer
    if (action.context && (action.context === 'grouped_tickets' || action.context === 'all')) {
      return state;
    }

    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const poolId = state.currentPool!.id;
    const updatedSelections = removeLastSelectionUtil(state.currentPoolSelections[poolId], action.legId);

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [poolId]: updatedSelections
      }
    };
  }),

  on(removeSelectionsInLeg, (state, action) => {
    // GT context is handled by the GroupTickets feature reducer
    if (action.context && (action.context === 'grouped_tickets' || action.context === 'all')) {
      return state;
    }

    // Pre-existing assumption: currentPool is set whenever this action fires — not independently verified
    const poolId = state.currentPool!.id;
    const updatedSelections = removeSelectionsUtil(state.currentPoolSelections[poolId], action.legId);

    return {
      ...state,
      currentPoolSelections: {
        ...state.currentPoolSelections,
        [poolId]: updatedSelections
      }
    };
  }),

  on(successPoolFeatured, (state, action) => {
    // Use entity lookup instead of legacy menu.pools
    const featuredPoolData = state.poolMenuEntities?.entities[action.featuredPoolResponse.pool_id];

    return {
      ...state,
      featuredPool: {
        image_url: action.featuredPoolResponse.image_url,
        pool_id: action.featuredPoolResponse.pool_id,
        pool: featuredPoolData
      }
    }
  }),

  on(clearPool, (state, action) => {
    return {
      ...state,
      currentPool: undefined,
      currentPoolLoading: false,
      currentPoolLoaded: false
    };
  }),

  on(successCoreSettings, (state, action) => {
    const poolCounts: Record<string, number> = { ...(state.poolsExtraModel.poolCounts || {}) };
    _.forEach(action.response.sport_categories, category => {
      poolCounts[category.code] = category.pools_count;
    });
    return {
      ...state,
      poolsExtraModel: {
        ...state.poolsExtraModel,
        OPEN_SYNDICATES: action.response.open_syndicate_count,
        CURRENCY_CODE: action.response.default_currency,
        poolCounts
      }
    }
  }),

  on(setPoolsExtraDetails, (state, action) => {
    return {
      ...state,
      poolsExtraModel: action.poolsExtraModel,
      filters: action.filters
    }
  }),

  on(successPoolsMenu, (state, action) => {
    // Extract pools array from parsed menu for entity adapter
    const poolsArray = Object.values(action.parsedPoolsMenu.pools || {});

    return {
      ...state,
      // Entity-managed pools (source of truth for individual pool data)
      // Use initialPoolMenuState as fallback if poolMenuEntities is undefined
      poolMenuEntities: poolMenuAdapter.setAll(poolsArray, state.poolMenuEntities || initialPoolMenuState),
      // Store full menu structure (selectPoolsMenu will reconstruct pools from entities)
      menu: action.parsedPoolsMenu,
      menuLoaded: true
    }
  }),

  on(successLeftMenu, (state, action) => {
    return {
      ...state,
      parsedLeftMenu: action.parsedLeftMenu,
      menuLoaded: true
    }
  }),


  on(successPoolsHeadline, (state, action) => {
    if (!action.coreSettings) return state; // called by session refresh on app start
    const coreSettings = action.coreSettings;
    const poolsHeadlineResponse = _.cloneDeep(action.poolsHeadlineResponse);
    const headlinePools: PoolsMenuScope.PoolsHeadline = _.keyBy(_.map(poolsHeadlineResponse, (pool) => {
      pool.code = _.result(_.find(coreSettings.sport_categories, { 'id': pool.sport_category_id }), 'code');
      return pool;
    }), 'code');

    return {
      ...state,
      headlinePools: headlinePools
    }
  }),

  on(setSportCode, (state, action) => {
    const newSportCode: any = Object.assign({}, state.currentSportCode, action.sportCode);
    return {
      ...state,
      currentSportCode: newSportCode
    }
  }),

  on(partialUpdate, (state, action) => {
    // Safety check: ensure action.event exists and matches a function in ws
    const handler = action.event ? ws[action.event] : undefined;
    if (typeof handler === 'function') {
      return handler(state, action);
    }
    return state;
  }),

  // sports & fantasy stats

  on(callSportsStats, (state, action) => {
    return {
      ...state,
      sportsStatsLoading: true
    }
  }),

    on(successSportsStats, (state, action) => {
      return {
        ...state,
        sportsStats: action.stats,
        sportsStatsLoading: false
      }
    }),

    on(callFantasyStats, (state, action) => {
      return {
        ...state,
        fantasyStatsLoading: true
      }
    }),

    on(successFantasyStats, (state, action) => {
      return {
        ...state,
        fantasyStats: action.stats,
        fantasyStatsLoading: false
      }
    }),
  // end sports & fantasy stats
);

type WsHandlers = {
  RELOAD_EVENTS: string[];
  [key: string]: ((state: MicrositePoolsState, action: any) => any) | string[];
};

export const ws: WsHandlers = {
  POOL_INFO_CHANGED: (state: MicrositePoolsState, action): any => {
    const newData = action.data;
    const poolId = newData.id;

    // Update pool menu using entity adapter (no cloneDeep needed!)
    const existingPool = state.poolMenuEntities?.entities[poolId];
    const shouldUpdateMenu = !!existingPool && new Big.Big(newData.headline_prize).gt(existingPool.headline_prize);

    const updatedMenuEntities = shouldUpdateMenu
      ? poolMenuAdapter.updateOne({
          id: poolId,
          changes: { headline_prize: newData.headline_prize }
        }, state.poolMenuEntities || initialPoolMenuState)
      : (state.poolMenuEntities || initialPoolMenuState);

    // Update current pool if it matches
    const isCurrentPool = !!state.currentPool && Number(state.currentPool.id) === Number(poolId);
    let updatedCurrentPool = state.currentPool;

    if (isCurrentPool && state.currentPool) {
      const changes: Partial<PoolScope.CurrentPool> = {};

      // Update num_units if larger
      try {
        if (new Big.Big(newData.num_units).gt(state.currentPool.num_units)) {
          changes.num_units = newData.num_units;
        }
      } catch (e) {
        console.error('[Pool Reducer] POOL_INFO_CHANGED: num_units comparison failed:', e);
      }

      // Update headline_prize if larger
      try {
        if (new Big.Big(newData.headline_prize).gt(state.currentPool.headline_prize)) {
          changes.headline_prize = newData.headline_prize;
        }
      } catch (e) {
        console.error('[Pool Reducer] POOL_INFO_CHANGED: headline_prize comparison failed:', e);
      }

      // Update prizes if larger
      try {
        if (newData.pool_prizes && state.currentPool.prizes) {
          const updatedPrizes = state.currentPool.prizes.map(prize => {
            const newPrize = newData.pool_prizes[prize.prize_type_code];
            if (newPrize && new Big.Big(newPrize.current_prize).gt(prize.amount)) {
              return { ...prize, amount: newPrize.current_prize };
            }
            return prize;
          });
          changes.prizes = updatedPrizes;
        }
      } catch (e) {
        console.error('[Pool Reducer] POOL_INFO_CHANGED: prizes update failed:', e);
      }

      // Apply changes immutably if any exist
      updatedCurrentPool = Object.keys(changes).length > 0
        ? { ...state.currentPool, ...changes }
        : state.currentPool;
    }

    return {
      ...state,
      poolMenuEntities: updatedMenuEntities,
      currentPool: updatedCurrentPool
    }
  },

  SPORT_EVENT_COMPETITORS_UPDATE: (state: MicrositePoolsState, action): any => {
    /* common */
    const newData = action.data;

    /* currentPool */
    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    const shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    // Pre-existing assumption: this update only fires when a current pool exists — not independently verified
    const leg = _.find(currentPoolToBeUpdated!.legs, { 'id': newData.data.leg_id });
    if (leg) {
      const new_sport_event_competitors = newData.data.sport_event_competitors;
      const current_sport_event_competitors = _.result(leg, 'sport_event.sport_event_competitors', []);
      _.forEach(new_sport_event_competitors, (competitor: any) => {
        let new_competitor: any = _.find(current_sport_event_competitors, { 'id': competitor.id });
        // Variable reassignment is intentional - updates competitor with merged data
        new_competitor = Object.assign(new_competitor, {
          withdrawn: competitor.withdrawn,
          probability: competitor.probability,
          fractional_probability: competitor.fractional_probability,
          favourite: competitor.favourite
        });
      });
    }


    return {
      ...state,
      currentPool: (shouldCurrentPoolBeUpdated && leg) ? currentPoolToBeUpdated : state.currentPool
    }
  },

  SPORT_EVENT_COMPETITOR_UPDATE: (state: MicrositePoolsState, action): any => {
    /* common */
    const newData = action.data;

    /* currentPool */
    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    const shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    if(shouldCurrentPoolBeUpdated) {
      const pool_running = (currentPoolToBeUpdated.status === 'IN_PLAY');
      const pool_open = (currentPoolToBeUpdated.status === 'OPEN');
      // let skip_update = false;
      _.forEach(currentPoolToBeUpdated.legs, (leg: any) => {
        if (leg.sport_event.id === newData.data.sport_event_id) {
          _.forEach(leg.sport_event.sport_event_competitors, (competitor: any) => {
            if (competitor.competitor_id === newData.data.competitor_id) {
              // LOGIC: If pool is IN_PLAY or OPEN and have ONLY price update - skip it
              if ((pool_running || pool_open) &&
                (competitor.cloth_number === newData.data.cloth_number) &&
                (competitor.favourite === newData.data.favourite) &&
                (competitor.name === newData.data.name) &&
                (competitor.result === newData.data.result) &&
                (competitor.status === newData.data.status) &&
                (competitor.withdrawn === newData.data.withdrawn) &&
                (JSON.stringify(competitor.add_info_json) === JSON.stringify(newData.data.add_info_json)) &&
                ((competitor.probability !== newData.data.probability) || (competitor.fractional_probability !== newData.data.fractional_probability))) {
                // skip_update = true;
              }
              competitor = Object.assign(competitor, {
                add_info_json: newData.data.add_info_json,
                cloth_number: newData.data.cloth_number,
                favourite: newData.data.favourite,
                fractional_probability: newData.data.fractional_probability,
                name: newData.data.name,
                probability: newData.data.probability,
                result: newData.data.result,
                status: newData.data.status,
                withdrawn: newData.data.withdrawn
              });
            }
          });
        }
      });
    }


    return {
      ...state,
      currentPool: shouldCurrentPoolBeUpdated ? currentPoolToBeUpdated : state.currentPool
    }
  },

  SPORT_EVENT_UPDATE: (state: MicrositePoolsState, action): any => {
    /* common */
    const newData = action.data;

    /* currentPool */
    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    let shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    if (currentPoolToBeUpdated && currentPoolToBeUpdated.type_code === 'FANTASY_MVP' && currentPoolToBeUpdated.sport_code === 'FANTASY_GAMES') {
      shouldCurrentPoolBeUpdated = false;
    }

    if(shouldCurrentPoolBeUpdated) {
      // shouldCurrentPoolBeUpdated true guarantees currentPoolToBeUpdated is defined (reassignment above only narrows true→false, never the reverse)
      _.forEach(currentPoolToBeUpdated!.legs, (leg: any) => {
        if (leg.sport_event.id === newData.data.id) {
          leg.sport_event = Object.assign(leg.sport_event, {
            add_info_json: newData.data.add_info_json,
            name: newData.data.name,
            sched_start: newData.data.sched_start,
            status: newData.data.status,
            period: newData.data.period,
            sport_code: newData.data.sport_code,
            sport_sub_code: newData.data.sport_sub_code
          });
        }
      });
    }

    return {
      ...state,
      currentPool: shouldCurrentPoolBeUpdated ? currentPoolToBeUpdated : state.currentPool
    }
  },

  METRIC_UPDATE: (state: MicrositePoolsState, action): any => {
    const newData = action.data;

    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    let shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    if (currentPoolToBeUpdated && currentPoolToBeUpdated.type_code !== 'FANTASY_MVP' && currentPoolToBeUpdated.sport_code !== 'FANTASY_GAMES') {
      shouldCurrentPoolBeUpdated = false;
    }

    try {
      if(shouldCurrentPoolBeUpdated) {
        // shouldCurrentPoolBeUpdated true guarantees currentPoolToBeUpdated is defined (reassignment above only narrows true→false, never the reverse)
        _.forEach(currentPoolToBeUpdated!.legs, (leg: any) => {
          if (leg.id === newData.data.leg_id) {
            _.forEach(leg.selections, (selection: any) => {
              if (selection.player.player_id === newData.data.player.player_id) {
                selection.player = Object.assign(selection.player, {
                  withdrawn: newData.data.player.withdrawn,
                  metric_value: newData.data.player.metric_value
                })
              }
            });
          }
        });
      }
      return {
        ...state,
        currentPool: shouldCurrentPoolBeUpdated ? currentPoolToBeUpdated : state.currentPool
      }
    } catch (e: any) {
      return {
        ...state,
        currentPool: state.currentPool
      }
    }

  },

  PLAYER_UPDATE: (state: MicrositePoolsState, action): any => {
    const newData = action.data;

    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    let shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    if (currentPoolToBeUpdated && currentPoolToBeUpdated.type_code !== 'FANTASY_MVP' && currentPoolToBeUpdated.sport_code !== 'FANTASY_GAMES') {
      shouldCurrentPoolBeUpdated = false;
    }

    try {
      if(shouldCurrentPoolBeUpdated) {
        // shouldCurrentPoolBeUpdated true guarantees currentPoolToBeUpdated is defined (reassignment above only narrows true→false, never the reverse)
        _.forEach(currentPoolToBeUpdated!.legs, (leg: any) => {
          if (leg.id === newData.data.leg_id) {
            _.forEach(leg.selections, (selection: any) => {
              if (selection.player.player_id === newData.data.player.player_id) {
                selection.player = Object.assign(selection.player, {
                  withdrawn: newData.data.player.withdrawn,
                  metric_value: newData.data.player.metric_value
                })
              }
            });
          }
        });
      }
      return {
        ...state,
        currentPool: shouldCurrentPoolBeUpdated ? currentPoolToBeUpdated : state.currentPool
      }
    } catch (e: any) {
      return {
        ...state,
        currentPool: state.currentPool
      }
    }

  },

  RESULTS_UPDATED: (state: MicrositePoolsState, action): any => {
    /* common */
    const newData = action.data;
    // const idOfElementToBeUpdated = newData.id;

    /* currentPool */
    const currentPoolToBeUpdated = _.cloneDeep(state.currentPool || undefined);
    const shouldCurrentPoolBeUpdated = !!currentPoolToBeUpdated && (Number(currentPoolToBeUpdated.id) === Number(newData.object_id));

    if(!shouldCurrentPoolBeUpdated) return state;

    const leg = _.find(currentPoolToBeUpdated.legs, { 'id': newData.data.leg_id });
    if (leg) {
      const new_results = newData.data.result;
      leg.result = new_results;
    }

    return {
      ...state,
      currentPool: currentPoolToBeUpdated
    }
  },


  RELOAD_EVENTS: [
    // Reload events for: PoolsListMiddleware, HeadlinePoolsMiddleware
    'POOL_STATUS_CHANGED', 'POOL_SHOW', 'POOL_HIDE',

    // UNIT_DISTRIBUTION_CHANGED and POOL_UPDATED are handled in effects
    // See pool.effects.ts line 337 - single pool refetch logic
    'UNIT_DISTRIBUTION_CHANGED', 'POOL_UPDATED',

    // Authentication events trigger full menu reload
    'MICROSITE_AUTHENTICATION_REFRESH', 'USER_REGISTER', 'USER_LOGIN', 'USER_LOGOUT'
  ]
}


export function micrositePoolsReducer(
  state: MicrositePoolsState | undefined,
  action: Action
): MicrositePoolsState {
  return reducer(state, action);
}
